export function pdf_report_lines(report = {}, rows = [], options = {}) {
  return has_structured_sections(report)
    ? structured_pdf_lines(report, options)
    : flat_pdf_lines(report, rows, options);
}

function has_structured_sections(report = {}) {
  return Array.isArray(report.sections) && report.sections.length > 0;
}

function flat_pdf_lines(report = {}, rows = [], options = {}) {
  const lines = [
    pdf_line(report_title(report), 40, 760, 15),
    pdf_line(`Category: ${empty_when_falsey(report.category_label)}`, 40, 736, 10),
    pdf_line(`Date range: ${empty_when_falsey(options.date_range)}    Scope: ${empty_when_falsey(options.scope)}`, 40, 720, 10),
    pdf_line(empty_when_falsey(report.description), 40, 704, 9),
    ...pdf_table_lines(headers_from_rows(rows), rows, 676),
  ];
  return lines.slice(0, 62);
}

function pdf_line(text = '', x = 40, y = 720, size = 9) {
  const line = {};
  line.text = text;
  line.x = x;
  line.y = y;
  line.size = size;
  return line;
}

function report_title(report = {}) {
  return report.title || 'Company report';
}

function structured_pdf_lines(report = {}, options = {}) {
  const state = { y: 760, lines: [] };
  state.lines.push({ type: 'rect', x: 36, y: 748, width: 552, height: 2, color: '12 81 68' });
  add_report_header(state, report, options);
  report.sections.forEach((section) => {
    add_section(state, section);
  });
  return state.lines.slice(0, 130);
}

function add_report_header(state, report = {}, options = {}) {
  add_line(state, report_title(report), 40, 16);
  add_line(state, `Category: ${empty_when_falsey(report.category_label)}`, 40, 9);
  add_line(state, `Date range: ${empty_when_falsey(options.date_range)}      Scope: ${empty_when_falsey(options.scope)}`, 40, 9);
  add_line(state, empty_when_falsey(report.description), 40, 9);
  state.y -= 6;
}

function add_section(state, section = {}) {
  state.y -= 8;
  add_line(state, section.title || 'Report section', 40, 13);
  add_wrapped_line(state, section.description || '', 40, 8, 98);
  state.y -= 6;
  section_renderer(section.type)(state, section);
  state.y -= 8;
}

function section_renderer(type = '') {
  const renderers = new Map([
    ['metrics', add_metric_section],
    ['table', add_table_section],
    ['grouped_entries', add_grouped_entry_section],
  ]);
  return renderers.get(type) || add_table_section;
}

function add_metric_section(state, section = {}) {
  (section.rows || []).forEach((row) => {
    add_text(state, row.metric || '', 56, state.y, 9);
    add_text(state, row.value ?? '', 236, state.y, 9);
    state.y -= 16;
  });
}

function add_table_section(state, section = {}) {
  const columns = (section.columns || []).slice(0, 5);
  if (is_case_summary(columns)) {
    add_case_summary_section(state, section);
    return;
  }

  add_standard_table_section(state, section, columns);
}

function add_standard_table_section(state, section = {}, columns = []) {
  const layout = table_layout(columns);
  add_table_header(state, layout);
  (section.rows || []).slice(0, 12).forEach((row) => {
    add_standard_table_row(state, layout, row);
  });
}

function add_standard_table_row(state, layout = [], row = {}) {
  layout.forEach((column) => {
    add_text(state, clipped_value(safe_value(row, column.key), column.width), column.x, state.y, 8);
  });
  state.y -= 15;
}

function add_case_summary_section(state, section = {}) {
  add_table_header(state, [
    { label: 'Matter / Case', x: 40, width: 330 },
    { label: 'Practice Area', x: 374, width: 86 },
    { label: 'Hours', x: 464, width: 52 },
    { label: 'Value', x: 520, width: 64 },
  ]);
  (section.rows || []).slice(0, 8).forEach((row) => {
    add_case_summary_row(state, row);
  });
}

function add_case_summary_row(state, row = {}) {
  const values = case_summary_values(row);
  state.lines.push({ type: 'rect', x: 36, y: state.y - 5, width: 552, height: 27, color: '248 250 252' });
  add_text(state, values.matter_id, 40, state.y, 8);
  add_text(state, clipped_value(values.practice_area, 86), 374, state.y, 8);
  add_text(state, values.total_hours, 464, state.y, 8);
  add_text(state, values.total_value, 520, state.y, 8);
  state.y -= 12;
  add_text(state, clipped_value(values.client_case_name, 330), 58, state.y, 8);
  state.y -= 17;
}

function case_summary_values(row = {}) {
  const values = {};
  values.matter_id = empty_when_falsey(row.matter_id);
  values.practice_area = row.practice_area;
  values.total_hours = empty_when_nullish(row.total_hours);
  values.total_value = empty_when_falsey(row.total_value);
  values.client_case_name = empty_when_falsey(row.client_case_name);
  return values;
}

function empty_when_falsey(value) {
  return value || '';
}

function empty_when_nullish(value) {
  return value ?? '';
}

function add_grouped_entry_section(state, section = {}) {
  (section.groups || []).slice(0, 4).forEach((group) => {
    add_grouped_entry(state, group);
  });
}

function add_grouped_entry(state, group = {}) {
  const group_lines = wrapped_lines(group.title || 'Matter', 78);
  state.lines.push({ type: 'rect', x: 52, y: state.y - 5, width: 520, height: 14 + (group_lines.length * 11), color: '234 246 241' });
  group_lines.forEach((line) => {
    add_line(state, line, 58, 9);
  });
  group.rows.slice(0, 4).forEach((row) => {
    add_detail_row(state, row);
  });
  state.y -= 2;
}

function add_detail_row(state, row = {}) {
  add_line(state, detail_row_summary(row), 66, 8);
  add_wrapped_line(state, detail_task_label(row), 78, 8, 88);
}

function detail_row_summary(row = {}) {
  return `${row.date || ''}   ${row.timekeeper || ''}   ${detail_hours(row)} hrs   ${row.total_value || ''}`;
}

function detail_hours(row = {}) {
  return Number(first_detail_hour_value(row)) || '';
}

function first_detail_hour_value(row = {}) {
  return [row.billable_hours, row.non_billable_hours, 0].find(Boolean);
}

function detail_task_label(row = {}) {
  return `Task: ${row.task || ''}`;
}

function add_table_header(state, columns = []) {
  state.lines.push({ type: 'rect', x: 36, y: state.y - 6, width: 552, height: 18, color: '12 81 68' });
  columns.forEach((column) => {
    add_text(state, clipped_value(column.label, column.width), column.x, state.y, 8, 'white');
  });
  state.y -= 22;
}

function add_line(state, text = '', x = 40, size = 9) {
  add_text(state, text, x, state.y, size);
  state.y -= size + 8;
}

function add_wrapped_line(state, text = '', x = 40, size = 9, max_chars = 92) {
  if (!text) return;
  wrapped_lines(text, max_chars).forEach((line) => {
    add_line(state, line, x, size);
  });
}

function add_text(state, text = '', x = 40, y = 720, size = 9, color = '') {
  if (state.y < 48) return;
  state.lines.push({ text, x, y, size, color });
}

function table_layout(columns = []) {
  const widths = table_widths(columns);
  let x = 40;
  return columns.map((column, index) => {
    const layout = { ...column, x, width: widths[index] || 100 };
    x += layout.width;
    return layout;
  });
}

function table_widths(columns = []) {
  const keys = columns.map((column) => column.key).join('|');
  return keys.includes('client_case_name')
    ? [88, 188, 92, 76, 96]
    : [148, 116, 100, 92, 92];
}

function is_case_summary(columns = []) {
  return columns.some((column) => column.key === 'client_case_name');
}

function clipped_value(value = '', width = 100) {
  const max_chars = Math.max(8, Math.floor(width / 4.6));
  const text = String(value ?? '');
  return text.length > max_chars ? `${text.slice(0, max_chars - 3)}...` : text;
}

function wrapped_lines(value = '', max_chars = 92) {
  const words = String(value ?? '').split(/\s+/).filter(Boolean);
  return words.reduce((lines, word) => append_word(lines, word, max_chars), ['']).filter(Boolean);
}

function append_word(lines = [], word = '', max_chars = 92) {
  const last = lines[lines.length - 1] || '';
  const next = last ? `${last} ${word}` : word;
  return next.length <= max_chars
    ? [...lines.slice(0, -1), next]
    : [...lines, word];
}

function pdf_table_lines(headers = [], rows = [], start_y = 676) {
  const columns = pdf_table_layout(headers.slice(0, 5));
  const header_background = { type: 'rect', x: 36, y: start_y - 6, width: 552, height: 18, color: '12 81 68' };
  const header_lines = columns.map((column) => pdf_header_line(column, start_y));
  const body_lines = rows.slice(0, 45).flatMap((row, row_index) =>
    columns.map((column) => pdf_body_line(row, column, row_index, start_y))
  );
  return [header_background, ...header_lines, ...body_lines];
}

function pdf_header_line(column, start_y) {
  const line = {};
  line.text = clipped_value(title_label(column.key), column.width);
  line.x = column.x;
  line.y = start_y;
  line.size = 8;
  line.color = 'white';
  return line;
}

function pdf_body_line(row, column, row_index, start_y) {
  const line = {};
  line.text = clipped_value(safe_value(row, column.key), column.width);
  line.x = column.x;
  line.y = start_y - 24 - (row_index * 16);
  line.size = 8;
  return line;
}

function pdf_table_layout(headers = []) {
  const widths = report_column_widths(headers);
  let x = 40;
  return headers.map((key, index) => {
    const column = { key, x, width: widths[index] || 100 };
    x += column.width;
    return column;
  });
}

function report_column_widths(headers = []) {
  const key = headers.join('|');
  if (key.includes('case_code') && key.includes('title')) {
    return [86, 178, 96, 110, 78];
  }
  return [110, 110, 110, 110, 108];
}

function headers_from_rows(rows = []) {
  return [...new Set(rows.flatMap((row) => Object.keys(row)))];
}

function safe_value(row = {}, key = '') {
  return safe_row_entry(row, key);
}

function safe_row_entry(row = {}, key = '') {
  const entry = Object.entries(row).find(([entry_key]) => entry_key === key);
  return entry_value(entry);
}

function entry_value(entry) {
  return Array.isArray(entry) && entry.length > 1 ? entry[1] ?? '' : '';
}

function title_label(value = '') {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
}
